import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Linkedin } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";
import { NAV_LINKS, SOCIALS } from "../../data/site";

const SocialIcon = ({ social, className }) => {
    if (social.slug) {
        return (
            <img
                src={`https://cdn.simpleicons.org/${social.slug}`}
                alt=""
                loading="lazy"
                className={className}
            />
        );
    }
    return <Linkedin className={className} strokeWidth={1.8} />;
};

const LEGAL = {
    privacy: {
        title: "Privacy Policy",
        body: "Apan Code Labs LLP collects only the information you share with us through this website (such as your name, email and project details) and uses it solely to respond to your enquiry. We do not sell, rent or share your data with third parties. You may request deletion of your information at any time by contacting us.",
    },
    terms: {
        title: "Terms of Use",
        body: "The content of this website is provided for general information about Apan Code Labs LLP and its services. Project scope, timelines and pricing are defined in individual written agreements. All trademarks and project names shown as concepts are placeholders and do not represent client work.",
    },
};

export default function Footer() {
    const [legal, setLegal] = useState(null);

    return (
        <footer data-testid="site-footer" className="border-t border-slate-200 bg-white">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-3">
                            <LogoMark />
                            <Wordmark />
                        </div>
                        <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-500">
                            Software development for the modern web.
                        </p>
                        <img
                            src="/brands/shopify-select-partner.png"
                            alt="Shopify Select Partner"
                            loading="lazy"
                            data-testid="footer-shopify-partner-badge"
                            className="mt-6 h-12 w-auto"
                        />
                    </div>
                    <nav className="md:col-span-3" aria-label="Footer">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Navigate</p>
                        <ul className="mt-5 space-y-3">
                            <li>
                                <Link to="/" data-testid="footer-link-home" className="text-sm text-slate-600 transition-colors duration-300 hover:text-brand">
                                    Home
                                </Link>
                            </li>
                            {NAV_LINKS.map((link) => (
                                <li key={link.to}>
                                    <Link
                                        to={link.to}
                                        data-testid={`footer-link-${link.label.toLowerCase()}`}
                                        className="text-sm text-slate-600 transition-colors duration-300 hover:text-brand"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                    <div className="md:col-span-4">
                        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400">Social</p>
                        <ul className="mt-5 space-y-3">
                            {SOCIALS.map((s) => (
                                <li key={s.label}>
                                    {s.url ? (
                                        <a
                                            href={s.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`footer-social-${s.label.toLowerCase()}`}
                                            className="flex items-center gap-2.5 text-sm text-slate-600 transition-colors duration-300 hover:text-brand"
                                        >
                                            <SocialIcon social={s} className="h-4 w-4" />
                                            {s.label}
                                        </a>
                                    ) : (
                                        <span
                                            data-testid={`footer-social-${s.label.toLowerCase()}`}
                                            className="flex items-center gap-2.5 text-sm text-slate-400"
                                            title="Profile URL to be added"
                                        >
                                            <SocialIcon social={s} className="h-4 w-4 opacity-50" />
                                            {s.label}
                                            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300">
                                                — soon
                                            </span>
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-slate-200 pt-8 sm:flex-row sm:items-center">
                    <p data-testid="footer-copyright" className="text-xs text-slate-400">
                        © 2026 Apan Code Labs LLP. All rights reserved.
                    </p>
                    <div className="flex gap-6">
                        <button
                            type="button"
                            data-testid="footer-privacy-btn"
                            onClick={() => setLegal("privacy")}
                            className="text-xs text-slate-400 transition-colors duration-300 hover:text-slate-900"
                        >
                            Privacy Policy
                        </button>
                        <button
                            type="button"
                            data-testid="footer-terms-btn"
                            onClick={() => setLegal("terms")}
                            className="text-xs text-slate-400 transition-colors duration-300 hover:text-slate-900"
                        >
                            Terms
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {legal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
                        onClick={() => setLegal(null)}
                        role="dialog"
                        aria-modal="true"
                        aria-label={LEGAL[legal].title}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 24, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 24, scale: 0.97 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            data-testid="legal-modal"
                            className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-hover"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between">
                                <h3 className="font-display text-xl font-semibold text-slate-900">{LEGAL[legal].title}</h3>
                                <button
                                    type="button"
                                    data-testid="legal-modal-close-btn"
                                    onClick={() => setLegal(null)}
                                    className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-slate-500 hover:text-slate-900"
                                    aria-label="Close"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="mt-4 text-sm leading-relaxed text-slate-600">{LEGAL[legal].body}</p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </footer>
    );
}
